/**
 * The builder's view of an intake schema: the client's numbered steps grouped
 * under the section they live in, plus the section-level edits the questions
 * card offers. Step-level edits reuse `stepModel` unchanged.
 */
import {
  AlignLeft,
  Calendar,
  CircleDot,
  FileUp,
  ListChecks,
  MapPin,
  Repeat,
  ToggleLeft,
  Type
} from "lucide-react";
import type { ComponentType } from "react";

import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeQuestionType
} from "@/api/types/outgoingIntake";
import { presendChecklist } from "@/features/requestsIde/editor/presend";
import {
  addCustomQuestion,
  addRepeatingGroup,
  finalize,
  findNode,
  locate,
  moveStep,
  stepsOf,
  updateNode,
  type Step
} from "@/features/requestsIde/editor/stepModel";

export const QUESTION_TYPES: {
  value: OutgoingIntakeQuestionType;
  label: string;
  icon: ComponentType<{ className?: string }>;
}[] = [
  { value: "short_text", label: "Text", icon: Type },
  { value: "long_text", label: "Long Text", icon: AlignLeft },
  { value: "boolean", label: "Yes / No", icon: ToggleLeft },
  { value: "date", label: "Date", icon: Calendar },
  { value: "single_select", label: "Choice", icon: CircleDot },
  { value: "multi_select", label: "Multi-Choice", icon: ListChecks },
  { value: "file", label: "Upload", icon: FileUp },
  { value: "address", label: "Address", icon: MapPin }
];

export const typeOf = (node: OutgoingIntakeFormNode) =>
  node.node_type === "repeatable_group"
    ? { label: "List", icon: Repeat }
    : QUESTION_TYPES.find((t) => t.value === node.question_type) ??
      QUESTION_TYPES[0];

export const hasOptions = (t: OutgoingIntakeQuestionType | null | undefined) =>
  t === "single_select" || t === "multi_select";

export type BuilderSection = {
  /** The section node's id, or a stable key for top-level steps. */
  id: string;
  /** null for steps that sit outside any section. */
  sectionId: string | null;
  title: string;
  steps: Step[];
};

/** Consecutive steps that share a section, in the client's order. */
export const builderSections = (
  schema: OutgoingIntakeFormNode[]
): BuilderSection[] => {
  const titles = new Map(
    schema
      .filter((n) => n.node_type === "section")
      .map((n) => [n.node_id, String(n.title ?? "").trim()])
  );
  const out: BuilderSection[] = [];
  for (const step of stepsOf(schema)) {
    const last = out[out.length - 1];
    if (last && last.sectionId === step.sectionId) {
      last.steps.push(step);
    } else {
      out.push({
        id: step.sectionId ?? `loose:${step.node.node_id}`,
        sectionId: step.sectionId,
        title: step.sectionId
          ? titles.get(step.sectionId) || "Untitled Section"
          : "Questions",
        steps: [step]
      });
    }
  }
  return out;
};

/** The first error the pre-send check finds on each step, by node id. */
export const issuesByNode = (
  schema: OutgoingIntakeFormNode[]
): Map<string, string> => {
  const out = new Map<string, string>();
  for (const item of presendChecklist(schema, "")) {
    if (item.severity === "error" && item.nodeId && !out.has(item.nodeId))
      out.set(item.nodeId, item.message);
  }
  return out;
};

/** Move a step one place up or down in the client's order. */
export const moveStepBy = (
  schema: OutgoingIntakeFormNode[],
  nodeId: string,
  delta: -1 | 1
): OutgoingIntakeFormNode[] => {
  const ids = stepsOf(schema).map((s) => s.node.node_id);
  const index = ids.indexOf(nodeId);
  if (index < 0) return schema;
  if (delta === -1) {
    if (index === 0) return schema;
    return moveStep(schema, nodeId, ids[index - 2] ?? null);
  }
  if (index === ids.length - 1) return schema;
  return moveStep(schema, nodeId, ids[index + 1]);
};

const topIndex = (schema: OutgoingIntakeFormNode[], sectionId: string) =>
  schema.findIndex((n) => n.node_id === sectionId);

export const renameSection = (
  schema: OutgoingIntakeFormNode[],
  sectionId: string,
  title: string
): OutgoingIntakeFormNode[] =>
  schema.map((n) => (n.node_id === sectionId ? { ...n, title } : n));

/** Drop a section and every question in it. */
export const removeSection = (
  schema: OutgoingIntakeFormNode[],
  sectionId: string
): OutgoingIntakeFormNode[] =>
  finalize(schema.filter((n) => n.node_id !== sectionId));

/** Swap a top-level section with its neighbour. */
export const moveSection = (
  schema: OutgoingIntakeFormNode[],
  sectionId: string,
  delta: -1 | 1
): OutgoingIntakeFormNode[] => {
  const from = topIndex(schema, sectionId);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= schema.length) return schema;
  const next = schema.slice();
  [next[from], next[to]] = [next[to], next[from]];
  return finalize(next);
};

const freeId = (schema: OutgoingIntakeFormNode[], base: string) => {
  let id = base;
  let n = 2;
  while (locate(schema, id)) {
    id = `${base}_${n}`;
    n += 1;
  }
  return id;
};

/**
 * A new section at the end holding one new question (an empty section isn't
 * kept). Returns the question's id so the card can open it.
 */
export const addSection = (
  schema: OutgoingIntakeFormNode[],
  title = "New Section"
): { schema: OutgoingIntakeFormNode[]; nodeId: string } => {
  const sectionId = freeId(schema, "custom.section");
  const nodeId = freeId(schema, "custom.new_question");
  return {
    schema: finalize([
      ...schema,
      {
        node_id: sectionId,
        node_type: "section",
        title,
        children: [
          {
            node_id: nodeId,
            node_type: "question",
            prompt: "New question",
            question_type: "short_text",
            required: false,
            help_text: null,
            options: [],
            model_path: null,
            template_source: "custom",
            children: []
          }
        ]
      }
    ]),
    nodeId
  };
};

/** A new question, upload or list at the end of a section. */
export const addToSection = (
  schema: OutgoingIntakeFormNode[],
  section: BuilderSection,
  kind: "question" | "upload" | "list"
): { schema: OutgoingIntakeFormNode[]; nodeId: string } => {
  const lastId = section.steps[section.steps.length - 1]?.node.node_id ?? null;
  if (kind === "list") return addRepeatingGroup(schema, "New list", lastId);
  return addCustomQuestion(
    schema,
    kind === "upload"
      ? { prompt: "Upload a document", question_type: "file" }
      : { prompt: "New question", question_type: "short_text" },
    lastId
  );
};

/**
 * A library question at the end of a section, unless the intake already asks
 * it (library questions keep their ids, which carry the record mapping).
 */
export const insertInSection = (
  schema: OutgoingIntakeFormNode[],
  section: BuilderSection,
  node: OutgoingIntakeFormNode
): { schema: OutgoingIntakeFormNode[]; added: boolean } => {
  if (findNode(schema, node.node_id)) return { schema, added: false };
  const fresh = { ...node, children: node.children ?? [] };
  if (section.sectionId) {
    const host = findNode(schema, section.sectionId);
    return {
      schema: finalize(
        updateNode(schema, section.sectionId, {
          children: [...(host?.children ?? []), fresh]
        })
      ),
      added: true
    };
  }
  const lastId = section.steps[section.steps.length - 1]?.node.node_id;
  const at = lastId ? schema.findIndex((n) => n.node_id === lastId) : -1;
  return {
    schema: finalize([
      ...schema.slice(0, at + 1),
      fresh,
      ...schema.slice(at + 1)
    ]),
    added: true
  };
};

/** "12 questions in 4 sections". */
export const sizeLine = (schema: OutgoingIntakeFormNode[]): string => {
  const sections = builderSections(schema);
  const steps = sections.reduce((n, s) => n + s.steps.length, 0);
  const named = sections.filter((s) => s.sectionId).length;
  const q = `${steps} question${steps === 1 ? "" : "s"}`;
  return named > 1 ? `${q} in ${named} sections` : q;
};
